import { EntryType } from 'model/common'
import {
  AudienceClauseProposition,
  LogicClauseProposition,
  LogicPropositionType,
  LogicClauseAsNumber,
  QuestionClauseProposition
} from 'model/questionLogic'
import { PersistentRespondentChoice } from 'model/persistentRespondentChoice'
import { isQuestionHiddenByDisplayLogic } from './hiddenByDisplayLogic'
import getPersistedRespondentChoices from './getPersistedRespondentChoices'
import { groupLogicPropositionsByQuestion } from './groupLogicPropositions.utils'

type ResponseOptionProposition =
  | QuestionClauseProposition
  | AudienceClauseProposition

// Questionnaire and audience propositions name the same pair and share one answer store, so they
// resolve identically.
const isResponseOptionProposition = (
  proposition: LogicClauseProposition
): proposition is ResponseOptionProposition =>
  proposition.propositionType ===
    LogicPropositionType.QuestionResponseOptionType ||
  proposition.propositionType ===
    LogicPropositionType.AudienceResponseOptionType

const matchResponseOptionProposition = (
  respondentId: string,
  surveyId: string,
  persistenRespondentChoices: PersistentRespondentChoice[],
  proposition: ResponseOptionProposition
) => {
  const { questionId, responseOptionId } = proposition.proposition

  const hasPreviousBeenHiddenByDisplayLogic = isQuestionHiddenByDisplayLogic(
    respondentId,
    surveyId,
    questionId
  )

  // A source on a fork branch this respondent did not take, or one that was skipped, produced no
  // answer — so do not show the entry that depends on it.
  const isPresentInPreviousResponses = persistenRespondentChoices.some(
    (persistenRespondentChoice) =>
      persistenRespondentChoice.questionId === questionId
  )

  if (hasPreviousBeenHiddenByDisplayLogic || !isPresentInPreviousResponses) {
    return false
  }

  const isPropositionInLocalstorage = persistenRespondentChoices.some(
    (persistenRespondentChoice) =>
      persistenRespondentChoice.questionId === questionId &&
      persistenRespondentChoice.responseChoiceIds.includes(responseOptionId)
  )

  return proposition.negated
    ? !isPropositionInLocalstorage
    : isPropositionInLocalstorage
}

export const checkIsPropositionInLocalstorageOrNot = (
  respondentId: string,
  surveyId: string,
  persistenRespondentChoices: PersistentRespondentChoice[],
  proposition: LogicClauseProposition
) => {
  const isPropositionNegated = proposition.negated

  if (isResponseOptionProposition(proposition)) {
    return matchResponseOptionProposition(
      respondentId,
      surveyId,
      persistenRespondentChoices,
      proposition
    )
  }

  if (
    proposition.propositionType ===
    LogicPropositionType.MatrixResponseOptionType
  ) {
    const matrixProposition = proposition.proposition

    // Checking if the question to which this current entity depends on has been hidden by display logic
    // if it has, skip this one too
    // if not, do the rest of the checks
    const hasPreviousBeenHiddenByDisplayLogic = isQuestionHiddenByDisplayLogic(
      respondentId,
      surveyId,
      matrixProposition.matrixTitleId
    )

    if (hasPreviousBeenHiddenByDisplayLogic) {
      return false
    }

    const isMatrixPropositionInLocalstorage = persistenRespondentChoices.some(
      (persistenRespondentChoice) =>
        persistenRespondentChoice.questionId ===
          matrixProposition?.questionId &&
        persistenRespondentChoice.responseChoiceIds.includes(
          matrixProposition.responseOptionId
        ) &&
        persistenRespondentChoice.matrixTitleId ===
          matrixProposition.matrixTitleId
    )

    // If proposition is negated, it should not exist in the local storage
    // Otherwise, it should exist in the local storage
    return isPropositionNegated
      ? !isMatrixPropositionInLocalstorage
      : isMatrixPropositionInLocalstorage
  }

  return false
}

/** Whether one proposition holds, negation applied. */
export type PropositionMatcher = (
  persistentRespondentChoices: PersistentRespondentChoice[],
  proposition: LogicClauseProposition
) => boolean

const hasValidProposition = (
  propositions: LogicClauseProposition[],
  persistentRespondentChoices: PersistentRespondentChoice[],
  matchProposition: PropositionMatcher
): boolean => {
  // Example 1: If the answer to Q1 is R1 OR R2
  // Example 2: If the answer to Q1 is NOT R1 OR R2
  // If negated clause block, every proposition should be valid
  //   - For Example 2, R1 or R2 should not be answered in Q1 in the local storage
  // Otherwise, one valid proposition is enough

  const negatedPropositions = propositions.filter(
    (proposition) => proposition.negated
  )

  const nonNegatedPropositions = propositions.filter(
    (proposition) => !proposition.negated
  )

  // Note: I have to add negatedPropositions.length > 0 so it will not give a false-positive result
  // Using .every on an empty array always returns `true`
  const everyPropositionIsValid =
    negatedPropositions.length > 0 &&
    negatedPropositions.every((proposition) =>
      matchProposition(persistentRespondentChoices, proposition)
    )

  const somePropositionIsValid =
    nonNegatedPropositions.length > 0 &&
    nonNegatedPropositions.some((proposition) =>
      matchProposition(persistentRespondentChoices, proposition)
    )

  // Since this is a "OR", one valid proposition is enough
  return everyPropositionIsValid || somePropositionIsValid
}

const getQuestionLogicClause = (
  propositionLists: LogicClauseProposition[][]
): number => {
  // E.g.
  //   If answer to Q1 is NOT R1
  //   AND
  //   If answer to Q2 is R1 OR R2 <-- the operator is saved in this item
  // E.g.
  //   If answer to Q1 is NOT R1 <-- the operator is saved either here
  //   OR
  //   If answer to Q2 is R1 OR R2 <-- or here

  // Getting the operator from the second proposition (clause block) is enough
  // propositionLists must be at least 2
  const secondProposition = propositionLists.length > 1 && propositionLists[1]

  return (
    (secondProposition && secondProposition[0]?.clause) ||
    LogicClauseAsNumber.OR
  )
}

export const showEntityBasedOnLogic = (
  respondentId: string,
  surveyId: string,
  propositionLists: LogicClauseProposition[][],
  persistentRespondentChoices: PersistentRespondentChoice[],
  matchProposition: PropositionMatcher = (choices, proposition) =>
    checkIsPropositionInLocalstorageOrNot(
      respondentId,
      surveyId,
      choices,
      proposition
    )
) => {
  // Determine whether the clause is AND or OR by getting it from any of the propositions (clause blocks)
  const isAndOperator =
    getQuestionLogicClause(propositionLists) === LogicClauseAsNumber.AND

  // If it is an AND operator, check every propositions condition
  // Else if it is an OR operator, check some propositions condition

  return isAndOperator
    ? propositionLists.every((propositions) =>
        hasValidProposition(
          propositions,
          persistentRespondentChoices,
          matchProposition
        )
      )
    : groupLogicPropositionsByQuestion(propositionLists).some((propositions) =>
        hasValidProposition(
          propositions,
          persistentRespondentChoices,
          matchProposition
        )
      )
}

const displayEntityBasedOnLogic = (
  respondentId: string,
  surveyId: string,
  entityLogic: LogicClauseProposition[][] | undefined,
  entryType: EntryType
) => {
  const persistenRespondentChoices =
    getPersistedRespondentChoices(respondentId, surveyId) ?? []

  if (entityLogic && entityLogic.length > 0) {
    if (
      entryType === EntryType.QuestionEntryType ||
      entryType === EntryType.MatrixEntryType ||
      entryType === EntryType.TextCardEntryType ||
      entryType === EntryType.TextHighlighterEntryType
    ) {
      const shouldDisplayEntity = showEntityBasedOnLogic(
        respondentId,
        surveyId,
        entityLogic,
        persistenRespondentChoices
      )
      return shouldDisplayEntity
    }

    return true // For now, always display if it is not a QuestionEntryType or a MatrixEntryType or TextCardEntryType
  }

  return true // Always display if there is no logic
}

export default displayEntityBasedOnLogic
